import { CalendarDays } from 'lucide-react';

import { formatHandledAt } from 'components/Kit/HandledQuotes/utils/formatHandledTooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';

import { BUILT_GROUP_LABEL, BUILT_REASON_LABELS } from '../constants';
import { formatBuiltLoadRow } from '../utils/formatBuiltLoadRow';
import { getBuiltLoadIdentifier } from '../utils/getBuiltLoadIdentifier';

type BuiltLoadRowProps = {
  suggestion: LoadBuildingSuggestions;
  ordinal: string;
  onOpenLoad: (loadIdentifier: string) => void;
};

// Read-only built card: lane and pickup day, plus a link that opens the built load. How and when
// it was built show on hover or focus, laid out like a handled quote's tooltip. No build action.
export function BuiltLoadRow({
  suggestion,
  ordinal,
  onOpenLoad,
}: BuiltLoadRowProps) {
  const { lane, pickupDay } = formatBuiltLoadRow(suggestion);
  const loadIdentifier = getBuiltLoadIdentifier(suggestion);
  const handledAt = formatHandledAt(suggestion.handledAt);
  const reasonLabel = suggestion.handledReason
    ? BUILT_REASON_LABELS[suggestion.handledReason]
    : BUILT_GROUP_LABEL;

  return (
    <Flex
      align='center'
      justify='between'
      gap='sm'
      className='w-full rounded border border-info-300 bg-info-50 px-2.5 py-1.5'
    >
      <Tooltip>
        <TooltipTrigger
          aria-disabled='true'
          className='min-w-0 flex-1 cursor-default border-none bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400'
        >
          <Flex align='start' gap='xs' className='min-w-0 w-full'>
            <Typography
              variant='body-xxs'
              textColor='muted'
              className='shrink-0 tabular-nums'
            >
              {ordinal}
            </Typography>
            <Flex direction='col' align='stretch' className='min-w-0 flex-1'>
              <Typography
                variant='body-xxs'
                className='truncate text-neutral-800'
              >
                {lane}
              </Typography>
              {pickupDay && (
                <Typography
                  variant='body-xxs'
                  textColor='muted'
                  className='truncate'
                >
                  {`Pickup ${pickupDay}`}
                </Typography>
              )}
            </Flex>
          </Flex>
        </TooltipTrigger>
        <TooltipPortal container={getDrumkitContainer()}>
          {/* Rows span the taskpane width, so the tooltip opens below them. A side one would clip. */}
          <TooltipContent
            side='bottom'
            align='start'
            className='w-fit max-w-[240px] whitespace-nowrap px-2.5 py-2'
          >
            <Flex direction='col' gap='xs' align='start'>
              <Typography variant='body-xs'>{reasonLabel}</Typography>
              {handledAt && (
                <Flex align='center' gap='xs'>
                  <CalendarDays
                    className='h-3 w-3 shrink-0 text-neutral-500'
                    aria-hidden
                  />
                  <Typography variant='body-xxs'>{handledAt}</Typography>
                </Flex>
              )}
            </Flex>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
      {loadIdentifier ? (
        <button
          type='button'
          onClick={() => onOpenLoad(loadIdentifier)}
          aria-label={`Open built load ${loadIdentifier}`}
          className='group shrink-0 cursor-pointer border-none bg-transparent p-0 text-accent-700 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-400'
        >
          <Typography
            as='span'
            variant='body-xxs'
            weight='medium'
            className='text-accent-700 group-hover:text-accent-800'
          >
            {`${BUILT_GROUP_LABEL}: ${loadIdentifier}`}
          </Typography>
        </button>
      ) : (
        <Typography variant='body-xxs' textColor='muted' className='shrink-0'>
          {BUILT_GROUP_LABEL}
        </Typography>
      )}
    </Flex>
  );
}
